const BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

// Carries structured error info (HTTP status + optional backend error code) so
// callers can branch on `status`/`code` instead of string-sniffing `message`.
// `message` may still hold raw backend prose — use getFriendlyErrorMessage()
// (client/lib/error-messages.ts) before showing anything to the user.
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  /** Per-field validation errors, when the backend returns `errors: [{field, message}]` (e.g. 400s). */
  readonly errors?: { field: string; message: string }[];

  constructor(message: string, status: number, code?: string, errors?: { field: string; message: string }[]) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.errors = errors;
  }
}

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("injaz_token");
}

// These endpoints answer 401 to mean "bad or absent credentials" (e.g. a wrong
// password), NOT "your session expired". Their 401 must surface to the caller
// so the form can show the server's error — it must NOT trigger the global
// session-expiry redirect, which on the Basic-Auth-protected staging origin
// re-presents the "site access" prompt.
const CREDENTIAL_ENDPOINTS = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
  "/api/auth/activate",
];

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  // SEC-04: primary auth is the httpOnly cookie — always send credentials.
  // A Bearer header is still attached as a transitional fallback for clients
  // that hold a token (kept until all callers rely solely on the cookie).
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  const isCredentialEndpoint = CREDENTIAL_ENDPOINTS.some((p) => path.startsWith(p));

  if (res.status === 401 && !isCredentialEndpoint) {
    // Genuine session expiry on a protected request. Clear cached state and ask
    // the app to redirect to /login via an event (handled by AuthProvider with a
    // client-side router push) — NOT a hard `window.location` reload, which on
    // the staging origin re-triggers the Basic-Auth ("site access") gate.
    if (typeof window !== "undefined") {
      localStorage.removeItem("injaz_token");
      localStorage.removeItem("injaz_user");
      window.dispatchEvent(new CustomEvent("injaz:session-expired"));
    }
    throw new Error("Session expired");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const { message, code, errors } = body as {
      message?: string;
      code?: string;
      errors?: { field: string; message: string }[];
    };
    throw new ApiError(message ?? "The request could not be completed.", res.status, code, errors);
  }

  return res.json() as Promise<T>;
}
